import { DocumentNode } from 'graphql';
import { CustomError } from 'ts-custom-error';
import { queryWithError, FetchError } from 'lib/graphql-client';
import { useEffect, useRef } from 'react';
import { isEqual } from 'lodash-es';
import useDataFetcher, {
  DataFetcherReturn,
  DataFetcherState,
} from '../data-fetcher';

export class TransformerError extends CustomError {
  constructor(message = 'GraphQL data does not conform to expected format') {
    super(message);
  }
}

function useDeepCompare(value: any): any {
  const ref = useRef();
  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

export type Transformer<T> = (data?: any) => T;

interface Options<V> {
  ignoreCall: (variables?: V) => boolean;
}

export default function useQuery<T, V = any>(
  query: DocumentNode,
  transform: Transformer<T>,
  variables?: V,
  options: Options<V> = { ignoreCall: () => false },
): DataFetcherReturn<T> {
  async function fetcher(): Promise<T> {
    const res = await queryWithError(query, variables);
    if (res.errors && !res.data) {
      throw new FetchError(undefined, res.errors);
    } else {
      console.error('partial error', res.errors);
    }

    if (!res.data) {
      throw new TransformerError();
    }

    return transform(res.data);
  }

  const [data, fetch, reset] = useDataFetcher<T>(() => fetcher());
  useEffect(() => {
    if (options.ignoreCall(variables)) return;
    fetch();
  }, [useDeepCompare(variables)]);
  return [data, fetch, reset];
}

export interface QueryWithPermissionsReturn<T, P> {
  data: T;
  permissions: P;
}

export function useQueryWithPermissions<T, P>(
  query: DocumentNode,
  variables = {},
  transform: Transformer<T>,
  permissions: Transformer<P>,
): DataFetcherState<QueryWithPermissionsReturn<T, P>> {
  async function fetcher(): Promise<QueryWithPermissionsReturn<T, P>> {
    const res = await queryWithError(query, variables);
    if (res.errors) {
      throw new FetchError(undefined, res.errors);
    }

    if (!res.data) {
      throw new TransformerError();
    }

    return {
      data: transform(res.data),
      permissions: permissions(res.data),
    };
  }

  const [data, fetch, reset] = useDataFetcher<QueryWithPermissionsReturn<T, P>>(
    () => fetcher(),
  );
  useEffect(() => {
    reset();
    fetch();
  }, [useDeepCompare(variables)]);
  return data;
}
